import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';

export type BannerBadgeType = 'discount' | 'offer' | 'sale' | 'promo' | 'limited' | 'custom';
export type BannerBadgeSize = 'small' | 'medium' | 'large';

export interface BannerBadgeConfig {
  primary: string;
  secondary: string;
  text: string;
  pattern: string;
  icon?: string;
}

@Component({
  selector: 'app-banner-badge',
  templateUrl: './banner-badge.component.html',
  standalone: false,
  styleUrls: ['./banner-badge.component.scss']
})
export class BannerBadgeComponent implements OnInit {
  @Input() value: string | number = '';
  @Input() amount: string | number = '160.00 €';
  @Input() label: string = 'Totale HT :';
  @Input() type: BannerBadgeType = 'discount';
  @Input() size: BannerBadgeSize = 'medium';
  @Input() disabled: boolean = false;
  @Input() animated: boolean = true;
  @Input() showIcon: boolean = true;
  @Input() customColor: string | null = null;
  @Input() customText: string | null = null;
  
  @Output() badgeClick = new EventEmitter<string | number>();

  private badgeConfigs: Record<BannerBadgeType, BannerBadgeConfig> = {
    discount: {
      primary: '#47C10D',
      secondary: '#5DD31A',
      text: '#ffffff',
      pattern: 'dots',
      icon: 'clock'
    },
    offer: {
      primary: '#ff6b35',
      secondary: '#ff8c42',
      text: '#ffffff',
      pattern: 'stripes',
      icon: '🎯'
    },
    sale: {
      primary: '#ff4757',
      secondary: '#ff6b7a',
      text: '#ffffff',
      pattern: 'waves',
      icon: '🔥'
    },
    promo: {
      primary: '#8b5cf6',
      secondary: '#a78bfa',
      text: '#ffffff',
      pattern: 'grid',
      icon: '✨'
    },
    limited: {
      primary: '#2ed573',
      secondary: '#7bed9f',
      text: '#000000',
      pattern: 'dots',
      icon: '⚡'
    },
    custom: {
      primary: '#f2c525',
      secondary: '#f4d03f',
      text: '#000000',
      pattern: 'dots',
      icon: '🎁'
    }
  };

  currentConfig: BannerBadgeConfig = this.badgeConfigs.discount;

  ngOnInit(): void {
    this.updateConfig();
  }

  ngOnChanges(): void {
    this.updateConfig();
  }

  private updateConfig(): void {
    if (this.type === 'custom' && this.customColor) {
      this.currentConfig = {
        primary: this.customColor,
        secondary: this.customColor,
        text: this.getContrastColor(this.customColor),
        pattern: 'dots',
        icon: '🎁'
      };
    } else {
      this.currentConfig = this.badgeConfigs[this.type];
    }
  }

  private getContrastColor(hexColor: string): string {
    // Convert hex to RGB
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    return luminance > 0.5 ? '#000000' : '#ffffff';
  }

  get displayValue(): string {
    if (this.customText) {
      return this.customText;
    }
    return this.value ? this.value.toString() : '';
  }

  get sizeClass(): string {
    return `banner-badge--${this.size}`;
  }

  get typeClass(): string {
    return `banner-badge--${this.type}`;
  }

  get animatedClass(): string {
    return this.animated ? 'banner-badge--animated' : '';
  }

  get dynamicStyles(): any {
    return {
      '--primary-color': this.currentConfig.primary,
      '--secondary-color': this.currentConfig.secondary,
      '--text-color': this.currentConfig.text,
      '--pattern-type': this.currentConfig.pattern
    };
  }

  onBadgeClick(): void {
    if (!this.disabled && this.value) {
      this.badgeClick.emit(this.value);
    }
  }
}
